/**
 * AccountDetail - Redesigned System Account Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation (Account Info, Permissions)
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
	BaseForm,
	PasswordBox,
	SwitchBox,
	TextBox,
} from '../../../../components/base';
import { EditModal, EditPageSection, EditPageField } from '../../../../components/container';
import { CheckedBoxListRole, SelectBoxUserGroup } from '../../../../components/shared/system';
import { UserGroupModel } from '../../../../context/models/users-group/UserGroupModel';
import { UserModel, df_UserModel, valid_UserModel } from '../../../../context/models/users/UserModel';
import { User, Shield, Key, Mail, UserCircle } from 'lucide-react';

type IAccountDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: UserModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: UserModel) => void;
};

const AccountDetail = (props: IAccountDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<UserModel>(df_UserModel);
	const [userGroupSelected, setUserGroupSelected] = useState<UserGroupModel | undefined>();
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<UserModel>({
		resolver: yupResolver(valid_UserModel(isAddMode) as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: UserModel) => {
		console.log(values);
		onSubmit({ ...initialValues, ...values });
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			const { avatar, email, firstName, fullName, id, isActive, lastName, midleName, phoneNumber, roles, userGroupId, userName } =
				detailModel;
			setInitialValues((prev) => ({
				...prev,
				avatar,
				email,
				firstName,
				fullName,
				id,
				isActive,
				lastName,
				midleName,
				phoneNumber,
				roles,
				userGroupId,
				userName,
			}));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	useEffect(() => {
		if (userGroupSelected) {
			setInitialValues((prev) => ({ ...prev, roles: userGroupSelected.roleIds?.split(',').map((x: any) => parseInt(x)) }));
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userGroupSelected]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof UserModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Account Info
	const accountInfoTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Họ và tên'
				required
				error={errors.fullName?.message}
				helpText='Tên đầy đủ của tài khoản'
			>
				<TextBox
					name='fullName'
					error={errors.fullName?.message}
					onValueChanged={(value) => updateField('fullName', value)}
					value={initialValues.fullName}
					hasValid={true}
					placeholder='Nguyễn Văn A'
				/>
			</EditPageField>

			<EditPageField
				label='Email'
				required
				error={errors.email?.message}
				helpText='Địa chỉ email liên hệ'
			>
				<TextBox
					name='email'
					type='email'
					error={errors.email?.message}
					onValueChanged={(value) => updateField('email', value)}
					value={initialValues.email}
					hasValid={true}
					placeholder='email@example.com'
				/>
			</EditPageField>

			<EditPageField
				label='Tài khoản'
				required
				error={errors.userName?.message}
				helpText='Tên tài khoản đăng nhập hệ thống'
			>
				<TextBox
					name='userName'
					error={errors.userName?.message}
					onValueChanged={(value) => updateField('userName', value)}
					isDisabled={!isAddMode}
					value={initialValues.userName}
					hasValid={true}
					placeholder='username'
				/>
			</EditPageField>

			<EditPageField
				label='Trạng thái tài khoản'
			>
				<SwitchBox
					name='isActive'
					label='Hoạt động'
					error={errors.isActive?.message}
					onValueChanged={(value) => updateField('isActive', value)}
					value={initialValues.isActive}
					hasValid={true}
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Password
	const passwordTab = (
		<div className='space-y-4'>
			<div className='p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800'>
				<p className='text-sm text-amber-800 dark:text-amber-200'>
					{isAddMode
						? 'Mật khẩu sẽ được gửi đến email của người dùng sau khi tạo tài khoản.'
						: 'Chỉ nhập mật khẩu mới khi bạn cần thay đổi. Để trống nếu giữ nguyên mật khẩu cũ.'}
				</p>
			</div>

			<EditPageField
				label='Mật khẩu mới'
				required={isAddMode}
				error={errors.password?.message}
				helpText={isAddMode ? 'Mật khẩu đăng nhập hệ thống' : 'Bỏ trống nếu giữ nguyên mật khẩu'}
			>
				<PasswordBox
					name='password'
					error={errors.password?.message}
					onValueChanged={(value) => updateField('password', value)}
					value={initialValues.password}
					hasValid={true}
					placeholder={isAddMode ? 'Nhập mật khẩu...' : 'Để trống nếu giữ nguyên'}
				/>
			</EditPageField>

			<EditPageField
				label='Xác nhận mật khẩu'
				required={isAddMode}
				error={errors.passwordConfirmed?.message}
			>
				<PasswordBox
					name='passwordConfirmed'
					error={errors.passwordConfirmed?.message}
					onValueChanged={(value) => updateField('passwordConfirmed', value)}
					value={initialValues.passwordConfirmed}
					hasValid={true}
					placeholder='Nhập lại mật khẩu...'
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Permissions
	const permissionsTab = (
		<div className='space-y-6'>
			<EditPageSection
				title='Nhóm tác nhân'
				icon={<Shield className='h-4 w-4' />}
			>
				<div className='space-y-4'>
					<p className='text-sm text-muted-foreground'>
						Chọn nhóm tác nhân để nhanh chóng gán các quyền có sẵn. Có thể tùy chỉnh thêm quyền bên dưới.
					</p>
					<SelectBoxUserGroup
						name='userGroupId'
						label='Tác nhân'
						error={errors.userGroupId?.message}
						hanldeOnValueChanged={(value, data) => {
							updateField('userGroupId', value);
							setUserGroupSelected(data);
						}}
						value={initialValues.userGroupId}
						hasValid={true}
						placeholder='Chọn tác nhân...'
					/>
				</div>
			</EditPageSection>

			<EditPageSection
				title='Quyền hạn'
				icon={<Shield className='h-4 w-4' />}
			>
				<CheckedBoxListRole
					name='roles'
					label='Danh sách quyền'
					hasValid={true}
					error={errors.roles?.message}
					onSelectionChanged={(value: number[]) => {
						updateField('roles', value?.map((x: any) => parseInt(x)));
					}}
					selectedValue={initialValues.roles}
				/>
			</EditPageSection>
		</div>
	);

	const tabs = [
		{
			id: 'account',
			label: 'Thông tin tài khoản',
			icon: <User className='h-4 w-4' />,
			content: accountInfoTab,
		},
		{
			id: 'password',
			label: 'Mật khẩu',
			icon: <Key className='h-4 w-4' />,
			content: passwordTab,
		},
		{
			id: 'permissions',
			label: 'Phân quyền',
			icon: <Shield className='h-4 w-4' />,
			content: permissionsTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới tài khoản hệ thống' : 'Chỉnh sửa tài khoản'}
			sizeClass='2xl'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Quản lý tài khoản', href: '/accounts' },
				{ label: isAddMode ? 'Tạo mới' : initialValues.fullName || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default AccountDetail;
